package com.fantasytenis.web.screens.contests

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.contests.entity.ContestEntity
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.ContestType
import com.fantasytenis.shared.domain.contests.entity.EntryStatus
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ContestStatusBadge
import com.fantasytenis.web.components.ContestTypeBadge
import com.fantasytenis.web.components.FeaturedBadge
import com.fantasytenis.web.components.GuaranteedBadge
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.components.SkeletonContestGrid
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.h2Style
import com.fantasytenis.web.theme.h3Style
import com.fantasytenis.web.utils.ContestUtils
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen displaying list of available contests.
 */
@Composable
fun ContestListScreen() {
    val scope = rememberCoroutineScope()
    val showPaidGames by SharedData.showPaidGames.collectAsState()
    var contests by remember { mutableStateOf<List<ContestEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selectedStatusFilter by remember { mutableStateOf<ContestStatus?>(null) }
    var selectedTypeFilter by remember { mutableStateOf<ContestType?>(null) }
    var searchQuery by remember { mutableStateOf("") }
    var userEnteredContestIds by remember { mutableStateOf<Set<String>>(emptySet()) }

    // Filter contests by search query and paid games setting
    val filteredContests = remember(contests, searchQuery, showPaidGames) {
        // Filter based on platform setting
        val visibleContests = if (showPaidGames) contests else contests.filter { it.entryFee == 0.0 }

        if (searchQuery.isBlank()) {
            visibleContests
        } else {
            val query = searchQuery.lowercase()
            visibleContests.filter { contest ->
                contest.name.lowercase().contains(query) ||
                contest.tournamentName.lowercase().contains(query)
            }
        }
    }

    // Load user entries on mount to track which contests user has entered
    LaunchedEffect(Unit) {
        DependencyContainer.getUserContestEntriesUseCase().fold(
            onSuccess = { entries ->
                userEnteredContestIds = entries
                    .filter { it.status != EntryStatus.CANCELLED }
                    .map { it.contestId }
                    .toSet()
            },
            onFailure = { /* Non-critical - continue without entry info */ }
        )
    }

    // Load contests on mount
    LaunchedEffect(selectedStatusFilter, selectedTypeFilter) {
        isLoading = true
        error = null

        val statusValue = selectedStatusFilter?.let { status ->
            when (status) {
                ContestStatus.UPCOMING -> 1
                ContestStatus.LIVE -> 2
                ContestStatus.COMPLETED -> 3
                ContestStatus.CANCELLED -> 4
            }
        }

        val typeValue = selectedTypeFilter?.let { type ->
            when (type) {
                ContestType.GPP -> 1
                ContestType.HEAD_TO_HEAD -> 2
                ContestType.FIFTY_FIFTY -> 3
                ContestType.MULTIPLIER -> 4
                ContestType.FREE -> 5
            }
        }

        DependencyContainer.listContestsUseCase(
            status = statusValue,
            type = typeValue,
            limit = 50
        ).fold(
            onSuccess = { list ->
                contests = list
                isLoading = false
            },
            onFailure = { e ->
                error = e.message
                isLoading = false
            }
        )
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Responsive Header with back button
        ResponsiveHeader(
            title = "Perfect Player",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.Home) }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(1200.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            // Search Bar
            Div({
                style {
                    marginBottom(16.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        backgroundColor(Colors.Surface)
                        borderRadius(8.px)
                        padding(12.px, 16.px)
                        property("box-shadow", "0 1px 3px rgba(0, 0, 0, 0.1)")
                    }
                }) {
                    // Search icon
                    Span({
                        style {
                            marginRight(12.px)
                            color(Colors.TextSecondary)
                            fontSize(16.px)
                        }
                    }) {
                        Text("\uD83D\uDD0D") // Magnifying glass
                    }

                    // Search input
                    org.jetbrains.compose.web.dom.TextInput(searchQuery) {
                        classes("baseline-input")
                        style {
                            flex(1)
                            border(0.px)
                            property("outline", "none")
                            fontSize(14.px)
                            backgroundColor(Color.transparent)
                            color(Colors.TextPrimary)
                        }
                        attr("placeholder", "Search contests by name or tournament...")
                        onInput { event ->
                            searchQuery = event.value
                        }
                    }

                    // Clear button (only show when there's a query)
                    if (searchQuery.isNotEmpty()) {
                        Span({
                            style {
                                marginLeft(12.px)
                                color(Colors.TextSecondary)
                                cursor("pointer")
                                fontSize(14.px)
                            }
                            onClick { searchQuery = "" }
                        }) {
                            Text("\u2715") // X symbol
                        }
                    }
                }
            }

            // Filters
            FiltersSection(
                selectedStatus = selectedStatusFilter,
                selectedType = selectedTypeFilter,
                onStatusChange = { selectedStatusFilter = it },
                onTypeChange = { selectedTypeFilter = it }
            )

            // Content
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        DependencyContainer.listContestsUseCase().fold(
                            onSuccess = { contests = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                filteredContests.isEmpty() -> EmptyState(
                    hasFilters = selectedStatusFilter != null || selectedTypeFilter != null,
                    hasSearch = searchQuery.isNotBlank(),
                    onClearFilters = {
                        selectedStatusFilter = null
                        selectedTypeFilter = null
                        searchQuery = ""
                    }
                )
                else -> ContestGrid(filteredContests, userEnteredContestIds)
            }
            }
        }
    }
}

@Composable
private fun FiltersSection(
    selectedStatus: ContestStatus?,
    selectedType: ContestType?,
    onStatusChange: (ContestStatus?) -> Unit,
    onTypeChange: (ContestType?) -> Unit
) {
    Div({
        style {
            marginBottom(24.px)
        }
    }) {
        // Status filters
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                marginBottom(12.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                    alignSelf(AlignSelf.Center)
                    marginRight(8.px)
                }
            }) {
                Text("Status:")
            }
            FilterChip("All", selectedStatus == null) { onStatusChange(null) }
            FilterChip("Upcoming", selectedStatus == ContestStatus.UPCOMING) {
                onStatusChange(ContestStatus.UPCOMING)
            }
            FilterChip("Live", selectedStatus == ContestStatus.LIVE) {
                onStatusChange(ContestStatus.LIVE)
            }
            FilterChip("Completed", selectedStatus == ContestStatus.COMPLETED) {
                onStatusChange(ContestStatus.COMPLETED)
            }
        }

        // Type filters
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                    alignSelf(AlignSelf.Center)
                    marginRight(8.px)
                }
            }) {
                Text("Type:")
            }
            FilterChip("All", selectedType == null) { onTypeChange(null) }
            FilterChip("GPP", selectedType == ContestType.GPP) {
                onTypeChange(ContestType.GPP)
            }
            FilterChip("H2H", selectedType == ContestType.HEAD_TO_HEAD) {
                onTypeChange(ContestType.HEAD_TO_HEAD)
            }
            FilterChip("50/50", selectedType == ContestType.FIFTY_FIFTY) {
                onTypeChange(ContestType.FIFTY_FIFTY)
            }
            // Only show Free filter when paid games are enabled (otherwise all games are free anyway)
            if (SharedData.showPaidGames.value) {
                FilterChip("Free", selectedType == ContestType.FREE) {
                    onTypeChange(ContestType.FREE)
                }
            }
        }
    }
}

@Composable
private fun FilterChip(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Div({
        style {
            padding(6.px, 12.px)
            borderRadius(16.px)
            cursor("pointer")
            fontSize(13.px)
            fontWeight(if (isSelected) 600 else 400)
            backgroundColor(if (isSelected) Colors.Primary else Colors.Surface)
            color(if (isSelected) Colors.White else Colors.TextPrimary)
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun LoadingState() {
    SkeletonContestGrid(count = 6)
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun EmptyState(
    hasFilters: Boolean = false,
    hasSearch: Boolean = false,
    onClearFilters: (() -> Unit)? = null
) {
    val hasAnyFilters = hasFilters || hasSearch

    Div({
        style {
            textAlign("center")
            padding(64.px, 24.px)
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            margin(24.px, 0.px)
        }
    }) {
        // Icon placeholder
        Div({
            style {
                fontSize(48.px)
                marginBottom(16.px)
            }
        }) {
            Text(if (hasSearch) "\uD83D\uDD0D" else "\uD83C\uDFBE") // Magnifying glass or Tennis ball
        }

        H3({
            style {
                fontSize(20.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(8.px)
            }
        }) {
            Text(
                when {
                    hasSearch -> "No contests found"
                    hasFilters -> "No matching contests"
                    else -> "No contests available"
                }
            )
        }

        P({
            style {
                color(Colors.TextSecondary)
                fontSize(14.px)
                marginBottom(24.px)
                lineHeight("1.5")
            }
        }) {
            when {
                hasSearch -> Text("No contests match your search. Try different keywords or clear your search.")
                hasFilters -> Text("Try adjusting your filters or check back later for new contests.")
                else -> Text("There are no contests scheduled at the moment. Check back soon for upcoming tournaments!")
            }
        }

        if (hasAnyFilters && onClearFilters != null) {
            Button(
                text = if (hasSearch && hasFilters) "Clear Search & Filters" else if (hasSearch) "Clear Search" else "Clear Filters",
                onClick = onClearFilters,
                variant = "secondary"
            )
        } else {
            Button(
                text = "Go to Home",
                onClick = { NavigationState.navigate(Route.Home) },
                variant = "primary"
            )
        }
    }
}

@Composable
private fun ContestGrid(contests: List<ContestEntity>, userEnteredContestIds: Set<String>) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
            gap(24.px)
        }
    }) {
        contests.forEach { contest ->
            ContestCard(contest, hasEntered = contest.id in userEnteredContestIds)
        }
    }
}

@Composable
private fun ContestCard(contest: ContestEntity, hasEntered: Boolean = false) {
    Div({
        classes("baseline-card")
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(20.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            cursor("pointer")
            property("transition", "transform 0.2s, box-shadow 0.2s")
        }
        onClick {
            NavigationState.navigate(Route.ContestDetail(contest.id))
        }
    }) {
        // Header with badges
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            // Type badge
            ContestTypeBadge(contest.contestType)

            // Status badge
            ContestStatusBadge(contest.status)
        }

        // Contest name
        H3({
            style {
                h3Style()
                marginBottom(4.px)
                color(Colors.TextPrimary)
            }
        }) {
            Text(ContestUtils.getDisplayName(contest.name))
        }

        // Tournament info
        if (contest.tournamentName.isNotBlank()) {
            P({
                style {
                    color(Colors.TextSecondary)
                    fontSize(13.px)
                    marginBottom(16.px)
                }
            }) {
                Text(contest.tournamentName)
            }
        }

        // Entry/Prize info row
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(12.px)
            }
        }) {
            val showPaidGamesValue = SharedData.showPaidGames.value
            InfoItem("Entry", if (contest.entryFee == 0.0 && showPaidGamesValue) "FREE" else if (contest.entryFee == 0.0) "$0" else "$${contest.entryFee}")
            InfoItem("Prize Pool", "$${contest.prizePool}")
        }

        // Entries progress
        Div({
            style {
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    marginBottom(4.px)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("Entries")
                }
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextPrimary)
                        fontWeight(500)
                    }
                }) {
                    Text("${contest.currentEntries} / ${contest.maxEntries}")
                }
            }

            // Progress bar
            Div({
                style {
                    width(100.percent)
                    height(4.px)
                    backgroundColor(Colors.Progress.Background)
                    borderRadius(2.px)
                    overflow("hidden")
                }
            }) {
                val fillPercent = (contest.currentEntries.toDouble() / contest.maxEntries * 100).coerceIn(0.0, 100.0)
                Div({
                    style {
                        width(fillPercent.percent)
                        height(100.percent)
                        backgroundColor(
                            when {
                                fillPercent > 90 -> Colors.Progress.Danger
                                fillPercent > 70 -> Colors.Progress.Warning
                                else -> Colors.Progress.Normal
                            }
                        )
                        property("transition", "width 0.3s")
                    }
                })
            }
        }

        // Additional info chips
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                flexWrap(FlexWrap.Wrap)
                marginBottom(16.px)
            }
        }) {
            if (contest.isGuaranteed) {
                InfoChip("Guaranteed")
            }
            if (contest.isFeatured) {
                InfoChip("Featured", highlight = true)
            }
            InfoChip("$${contest.salaryCap}K Cap")
        }

        // Enter/View button
        when {
            hasEntered -> {
                Button(
                    text = "View Entry",
                    onClick = {
                        NavigationState.navigate(Route.ContestDetail(contest.id))
                    },
                    variant = "secondary"
                )
            }
            contest.canEnter -> {
                Button(
                    text = "Enter Contest",
                    onClick = {
                        NavigationState.navigate(Route.ContestDetail(contest.id))
                    },
                    variant = "primary"
                )
            }
            contest.status == ContestStatus.LIVE -> {
                Button(
                    text = "View Live",
                    onClick = {
                        NavigationState.navigate(Route.ContestDetail(contest.id))
                    },
                    variant = "secondary"
                )
            }
        }
    }
}

// Badge components are now imported from com.fantasytenis.web.components.Badge

@Composable
private fun InfoItem(label: String, value: String) {
    Div {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(2.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(16.px)
                fontWeight(600)
                color(Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun InfoChip(label: String, highlight: Boolean = false) {
    Span({
        style {
            backgroundColor(if (highlight) Colors.Secondary else Colors.SurfaceVariant)
            color(if (highlight) Colors.TextPrimary else Colors.TextSecondary)
            fontSize(11.px)
            fontWeight(500)
            padding(4.px, 8.px)
            borderRadius(12.px)
        }
    }) {
        Text(label)
    }
}
